import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useChatStore } from "../../stores/chatStore";
import { useSSE } from "../../hooks/useSSE";
import { api } from "../../lib/api";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { TypingIndicator } from "./TypingIndicator";
import { ProgressSidebar } from "./ProgressSidebar";
import { ConnectionStatus } from "./ConnectionStatus";
import { EscapeHatches } from "./EscapeHatches";
import { CheckoutResultBanner } from "./CheckoutResultBanner";

export function ChatContainer() {
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialFetchDone = useRef(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [currentBuildId, setCurrentBuildId] = useState<string | null>(null);
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [siteStatus, setSiteStatus] = useState<string | null>(null);
  
  // Checkout result from query params
  const checkoutResult = searchParams.get("checkout") as "success" | "cancel" | null;

  const {
    messages,
    currentSpec,
    aiState,
    isStreaming,
    streamingText,
    error,
    isConnected,
    setSiteId,
    setMessages,
    setSpec,
    setAIState,
    setError,
    reset,
  } = useChatStore();

  const { sendMessage, cancel } = useSSE({
    url: api.getChatUrl(siteId!),
  });

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  // Load site data on mount
  useEffect(() => {
    if (!siteId) {
      navigate("/");
      return;
    }

    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    setSiteId(siteId);

    const loadSite = async () => {
      try {
        // Load site data and chat history in parallel
        const [siteResult, historyResult] = await Promise.all([
          api.getSite(siteId),
          api.getChatHistory(siteId).catch(() => ({ messages: [] })),
        ]);

        const { site } = siteResult;
        if (site.aiState) {
          setAIState(site.aiState);
        }
        if (site.currentBuildId) {
          setCurrentBuildId(site.currentBuildId);
        }
        if (site.subdomain) {
          setSubdomain(site.subdomain);
        }
        if (site.status) {
          setSiteStatus(site.status);
        }
        if (site.currentSpecId) {
          const { spec } = await api.getSpec(siteId);
          setSpec(spec);
        }

        // Load existing chat history
        if (historyResult.messages.length > 0) {
          setMessages(historyResult.messages.map(m => ({
            ...m,
            role: m.role as "user" | "assistant",
          })));
        }
        setHistoryLoaded(true);
      } catch (err) {
        console.error("Failed to load site:", err);
        setError("Failed to load site. Please try again.");
      }
    };

    loadSite();
  }, [siteId, navigate, setSiteId, setMessages, setSpec, setAIState, setError]);

  // Handle checkout result - refetch site data on success
  useEffect(() => {
    if (checkoutResult === "success" && siteId) {
      // Refetch site to get updated status
      const refetchSite = async () => {
        try {
          const { site } = await api.getSite(siteId);
          if (site.status) {
            setSiteStatus(site.status);
          }
        } catch (err) {
          console.error("Failed to refetch site after checkout:", err);
        }
      };
      refetchSite();
    }
  }, [checkoutResult, siteId]);

  // Dismiss checkout result banner
  const dismissCheckoutResult = () => {
    searchParams.delete("checkout");
    setSearchParams(searchParams, { replace: true });
  };

  // Send initial greeting if no messages (only after history check completes)
  useEffect(() => {
    if (siteId && messages.length === 0 && !isStreaming && historyLoaded) {
      // No existing messages, trigger AI greeting
      sendMessage("", siteId);
    }
  }, [siteId, messages.length, isStreaming, historyLoaded, sendMessage]);

  const handleSend = (text: string) => {
    if (!siteId || !text.trim()) return;
    sendMessage(text, siteId);
  };

  const handleSkip = () => {
    if (!siteId) return;
    sendMessage("skip", siteId);
  };

  const handleReset = () => {
    if (
      confirm(
        "Are you sure you want to start over? Your progress will be lost."
      )
    ) {
      cancel();
      reset();
      navigate("/");
    }
  };

  const handleHelp = () => {
    // Open help in a new tab or show a modal
    window.open("mailto:support@imonsite.co.uk?subject=Help with my website", "_blank");
  };

  if (!siteId) {
    return null;
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <ProgressSidebar
        currentState={aiState}
        spec={currentSpec}
        siteId={siteId}
        currentBuildId={currentBuildId}
        subdomain={subdomain}
        onSubdomainChange={setSubdomain}
        siteStatus={siteStatus}
      />

      {/* Main chat area */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Connection status */}
        <ConnectionStatus isConnected={isConnected} />

        {/* Checkout result banner */}
        {checkoutResult && (
          <CheckoutResultBanner
            result={checkoutResult}
            subdomain={subdomain}
            onDismiss={dismissCheckoutResult}
          />
        )}

        {/* Mobile header (visible on small screens) */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border">
          <a
            href="/"
            className="font-display text-lg font-medium tracking-tight text-primary"
          >
            ImOnSite
          </a>
          <span className="text-xs text-muted-foreground capitalize">
            {aiState.replace(/_/g, " ")}
          </span>
        </header>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
            {/* Empty state */}
            {messages.length === 0 && !isStreaming && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center size-16 rounded-full bg-accent/10 mb-4">
                  <svg
                    className="size-8 text-accent"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                    />
                  </svg>
                </div>
                <h2 className="font-display text-xl font-medium text-foreground mb-2">
                  Let's build your website
                </h2>
                <p className="text-muted-foreground">
                  Starting up the conversation...
                </p>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} />
            ))}

            {/* Streaming message */}
            {isStreaming && streamingText && (
              <ChatMessage
                message={{
                  id: "streaming",
                  siteId: siteId,
                  role: "assistant",
                  content: streamingText,
                  createdAt: new Date().toISOString(),
                }}
                isStreaming
              />
            )}

            {/* Typing indicator */}
            {isStreaming && !streamingText && <TypingIndicator />}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center justify-between px-4 py-3 bg-destructive/10 border-t border-destructive/20">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-destructive underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Escape hatches */}
        <EscapeHatches
          visible={messages.length > 3}
          onSkip={handleSkip}
          onReset={handleReset}
          onHelp={handleHelp}
        />

        {/* Input */}
        <ChatInput
          onSend={handleSend}
          disabled={isStreaming}
          placeholder={isStreaming ? "Thinking..." : "Type your message..."}
        />
      </main>
    </div>
  );
}

